import api from './api';
import apiGraphQL from './apiGraphQL';
import {DateTime} from 'luxon';
import {TeamSessionExportTypes} from '../enums';
import {PlayerSessionExportData} from '../services/reportService';

// GraphQL response types
interface TeamSessionExportDataResponse {
  id: number;
  startTime: string;
  teamId: number;
  type: string;
  playerSessions: PlayerSessionExportData[];
}

interface TeamSessionGroupExportDataResponse {
  id: number;
  startTime: string;
  teamId: number;
  teamSessions: TeamSessionExportDataResponse[];
}

const getFormatSubstring = (format: string) => {
  if (format === TeamSessionExportTypes.hudlSportscode) {
    return '-hudl-sportscode';
  } else if (format === TeamSessionExportTypes.spiideoPerform) {
    return '-spiideo-perform';
  } else if (format === TeamSessionExportTypes.catapultThunder) {
    return '-catapult-thunder';
  } else if (format === TeamSessionExportTypes.csvPlayers) {
    return '-players';
  } else if (format === TeamSessionExportTypes.csvShifts) {
    return '-shifts';
  } else {
    return '';
  }
};

export function getPlayerSessionExport({
  playerSessionIds,
  playerSessionStartTime,
  format,
}: {
  playerSessionIds: number[];
  playerSessionStartTime: string;
  format: TeamSessionExportTypes.csvPlayers | TeamSessionExportTypes.csvShifts;
}) {
  return new Promise<void>((resolve, reject) => {
    try {
      api
        .get('/util/export', {
          responseType: 'blob',
          params: {
            playerSessionIds: JSON.stringify(playerSessionIds),
            format,
          },
        })
        .then(response => {
          // create file link in browser's memory
          const href = URL.createObjectURL(response.data);

          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          // create the export name
          const filename = `HELIOS-playersession${getFormatSubstring(format)}-export-${DateTime.fromISO(playerSessionStartTime).toFormat('yyyy-MM-dd-HH-mm')}.${format.startsWith('csv') ? 'csv' : 'xml'}`;
          link.setAttribute('download', filename); // or any other extension
          document.body.appendChild(link);
          link.click();

          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          resolve();
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getTeamSessionExport({
  teamSessionId,
  teamSessionStartTime,
  format,
}: {
  teamSessionId: number;
  teamSessionStartTime: string;
  format:
    | TeamSessionExportTypes.csvPlayers
    | TeamSessionExportTypes.csvShifts
    | TeamSessionExportTypes.hudlSportscode
    | TeamSessionExportTypes.spiideoPerform
    | TeamSessionExportTypes.catapultThunder;
}) {
  return new Promise<void>((resolve, reject) => {
    try {
      api
        .get('/util/export', {
          responseType: 'blob',
          params: {
            teamSessionId,
            // replace spiideoPerform with hudlSportscode
            format:
              format === TeamSessionExportTypes.spiideoPerform
                ? TeamSessionExportTypes.hudlSportscode
                : format,
          },
        })
        .then(response => {
          // create file link in browser's memory
          const href = URL.createObjectURL(response.data);

          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          // create the export name
          const filename = `HELIOS-teamsession${getFormatSubstring(format)}-export-${DateTime.fromISO(teamSessionStartTime).toFormat('yyyy-MM-dd-HH-mm')}.${format.startsWith('csv') ? 'csv' : 'xml'}`;
          link.setAttribute('download', filename); // or any other extension
          document.body.appendChild(link);
          link.click();

          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          resolve();
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getTeamSessionGroupExport({
  teamSessionGroupId,
  teamSessionGroupStartTime,
  format,
}: {
  teamSessionGroupId: number;
  teamSessionGroupStartTime: string;
  format:
    | TeamSessionExportTypes.csvPlayers
    | TeamSessionExportTypes.csvShifts
    | TeamSessionExportTypes.hudlSportscode
    | TeamSessionExportTypes.spiideoPerform
    | TeamSessionExportTypes.catapultThunder;
}) {
  return new Promise<void>((resolve, reject) => {
    try {
      api
        .get('/util/export', {
          responseType: 'blob',
          params: {
            teamSessionGroupId,
            // replace spiideoPerform with hudlSportscode
            format:
              format === TeamSessionExportTypes.spiideoPerform
                ? TeamSessionExportTypes.hudlSportscode
                : format,
          },
        })
        .then(response => {
          // create file link in browser's memory
          const href = URL.createObjectURL(response.data);

          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          // create the export name
          const filename = `HELIOS-teamsessiongroup${getFormatSubstring(format)}-export-${DateTime.fromISO(teamSessionGroupStartTime).toFormat('yyyy-MM-dd-HH-mm')}.${format.startsWith('csv') ? 'csv' : 'xml'}`;
          link.setAttribute('download', filename); // or any other extension
          document.body.appendChild(link);
          link.click();

          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          resolve();
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getPlayerSessionDocuments({
  playerSessionIds,
  fileTitle,
}: {
  playerSessionIds: number[];
  fileTitle: string;
}) {
  return new Promise<boolean>((resolve, reject) => {
    try {
      api
        .get('/util/playerSessionDocuments', {
          responseType: 'blob',
          params: {
            playerSessionIds: JSON.stringify(playerSessionIds),
          },
        })
        .then(response => {
          const href = URL.createObjectURL(response.data);

          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', fileTitle);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          resolve(true);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

// GraphQL fragment for player session shift export data
const playerSessionShiftExportFragment = `
  id
  start
  end
  localDisplayStartTime
  startTime
  endTime
  valid
  timeOnIce
  strintTotalTime
  energyRatio
  strideCountTotal
  strideCountLeft
  strideCountRight
  speedAvgV2
  speedMaxV2
  accelAvgV2
  accelMaxV2
  strintFilteredStrideRateAvgV2
  strintFilteredStrideRateMaxV2
  balanceNormalized
  hrAvg
  hrMax
  hrStart
  hrEnd
  cumulative3DPlayerLoadV2
`;

// GraphQL fragment for player session export data
const playerSessionExportFragment = `
  id
  type
  startTime
  endTime
  localDisplayStartTime
  activeTime
  timeOnIce
  strintTotalTime
  energyRatio
  strideCountLeft
  strideCountRight
  speedAvgV2
  speedMaxV2
  accelAvgV2
  accelMaxV2
  strintFilteredStrideRateAvgV2
  strintFilteredStrideRateMaxV2
  balanceNormalized
  cumulative3DPlayerLoadV2
  hrAvg
  hrMax
  hustleScoreTopLevel
  position
  timeZone
  teamSessionId
  firstName
  lastName
  profilePosition
  playerNumber
  playerSessionShifts {
    ${playerSessionShiftExportFragment}
  }
`;

// Fetch player session export data via GraphQL
export function fetchPlayerSessionExportData(
  playerSessionIds: number[],
): Promise<PlayerSessionExportData[]> {
  return new Promise((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `
            query playerSessionExportData($playerSessionIds: [Int!]!) {
              playerSessionExportData(playerSessionIds: $playerSessionIds) {
                ${playerSessionExportFragment}
              }
            }
          `,
          variables: {playerSessionIds},
        })
        .then(response => {
          if (response.data.errors) {
            reject(new Error(response.data.errors[0].message));
            return;
          }
          resolve(response.data.data.playerSessionExportData);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

// Fetch team session export data via GraphQL
export function fetchTeamSessionExportData(
  teamSessionId: number,
): Promise<TeamSessionExportDataResponse> {
  return new Promise((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `
            query teamSessionExportData($teamSessionId: Int!) {
              teamSessionExportData(teamSessionId: $teamSessionId) {
                id
                startTime
                teamId
                type
                playerSessions {
                  ${playerSessionExportFragment}
                }
              }
            }
          `,
          variables: {teamSessionId},
        })
        .then(response => {
          if (response.data.errors) {
            reject(new Error(response.data.errors[0].message));
            return;
          }
          resolve(response.data.data.teamSessionExportData);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

// Fetch team session group export data via GraphQL
export function fetchTeamSessionGroupExportData(
  teamSessionGroupId: number,
): Promise<TeamSessionGroupExportDataResponse> {
  return new Promise((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `
            query teamSessionGroupExportData($teamSessionGroupId: Int!) {
              teamSessionGroupExportData(teamSessionGroupId: $teamSessionGroupId) {
                id
                startTime
                teamId
                teamSessions {
                  id
                  startTime
                  teamId
                  type
                  playerSessions {
                    ${playerSessionExportFragment}
                  }
                }
              }
            }
          `,
          variables: {teamSessionGroupId},
        })
        .then(response => {
          if (response.data.errors) {
            reject(new Error(response.data.errors[0].message));
            return;
          }
          resolve(response.data.data.teamSessionGroupExportData);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}
